import React from 'react';
import { Bar } from 'react-chartjs-2';
import { ChartData, ChartOptions } from 'chart.js';
import { useTranslation } from 'react-i18next';
import { secondsToHoursText, timeStringToSeconds } from '../Utility/timeHelpers';

interface ClientHourItem {
  id: number;
  name: string;
  summarize?: {
    task: { name: string };
    hour_spent?: string | null;
  }[];
}

interface ClientNoHourItem {
  id: number;
  name: string;
  hour_spent?: string | null;
}

interface WorkPerformanceGraphProps {
  clientHour: ClientHourItem[];
  clientNoHour: ClientNoHourItem[];
  onShowClientHourChange: (value: boolean) => void;
  showClientHour: boolean;
}

const WorkPerformanceGraph: React.FC<WorkPerformanceGraphProps> = ({
  clientHour,
  clientNoHour,
  onShowClientHourChange,
  showClientHour,
}) => {
  const { t } = useTranslation('client');

  // ADM-21（PR #224 の照合、HR-AC3）。集計 API の時間を秒に直し、CSV と同じ関数で小数2桁の時間にする。
  // 以前は「時＋分/60＋秒/3600」を toFixed(2) で丸め、ちょうど半分の値で CSV と食い違った。
  const parseDurationToHours = (duration: string) => secondsToHoursText(timeStringToSeconds(duration));

  // EV-337（2026-10-14・10-15 PM 判断: 英語は整数の 1 だけ単数、ほかは複数）。整数の「1」だけ「hour」、
  // 小数の「1.00」は英語の慣用どおり「1.00 hours」、0・0.5・1.5 なども「hours」（graph.hours_value の _one・_other）。
  // 出す数の形（「1.00」・「1」）は変えない。
  const hoursValue = (value: string | number) =>
    t('graph.hours_value', { value, count: String(value) === '1' ? 1 : 2 });

  // ADM-21。以前は10時間未満の作業を一律10として描いていたため、
  // 棒の長さが実時間と一致せず、短い作業ほど過大に見えていた。
  // 描画は実測値のみを使い、小さな値は凡例と数値表示で補う。
  const hourSpentData = clientHour.map((item) => {
    const tasks =
      item.summarize
        ?.map((summary) => {
          const hours = summary.hour_spent
            ? parseFloat(parseDurationToHours(summary.hour_spent))
            : 0;
          return {
            taskId: summary.task.name,
            actualValue: hours,
            displayValue: hours,
          };
        })
        .filter((task) => task.displayValue > 0) || [];
    return {
      clientId: item.id,
      clientName: item.name,
      tasks: tasks,
    };
  });

  const colors = [
    '#EDDDC0',
    '#A9C7F8',
    '#A26065',
    '#D4898F',
    '#D4AD07',
    '#6FE4B3',
    '#7DB826',
    '#196E69',
    '#6B9E5C',
    '#56DFBD',
    '#184863',
    '#F9FE7F',
    '#F1C2E0',
    '#4E9C7F',
    '#0331D1',
  ];

  const taskColorMap: { [key: string]: string } = {};
  let colorIndex = 0;

  clientHour.forEach((client) => {
    client.summarize?.forEach((summary) => {
      if (!taskColorMap[summary.task.name]) {
        taskColorMap[summary.task.name] = colors[colorIndex % colors.length];
        colorIndex++;
      }
    });
  });

  const chartData: ChartData<'bar'> = {
    labels: showClientHour
      ? hourSpentData.map((d) => d.clientName)
      : clientNoHour.map((item) => item.name),
    datasets: showClientHour
      ? hourSpentData.flatMap((client, clientIndex) =>
          client.tasks.map((task, taskIndex) => ({
            label: task.taskId,
            data: hourSpentData.map((_, i) =>
              i === clientIndex ? task.displayValue : 0
            ),
            backgroundColor: taskColorMap[task.taskId],
            borderColor: taskColorMap[task.taskId],
            borderWidth: 1,
            barPercentage: 0.9,
            categoryPercentage: 0.9,
          }))
        )
      : [
          {
            label: 'ClientNoHour',
            data: clientNoHour.map((item) =>
              item.hour_spent
                ? parseFloat(parseDurationToHours(item.hour_spent))
                : 0
            ),
            backgroundColor: colors.map((color) => `${color}90`),
            borderColor: colors,
            borderWidth: 1,
            barPercentage: 0.9,
            categoryPercentage: 0.9,
          },
        ],
  };

  const getMaxValue = (data: number[]) => {
    const max = Math.max(...data);
    if (max <= 40) return 10;
    if (max <= 300) return 50;
    if (max <= 800) return 100;
    return 200;
  };

  const clientNoHourData = clientNoHour.map((item) =>
    item.hour_spent ? parseFloat(parseDurationToHours(item.hour_spent)) : 0
  );
  const clientHourData = hourSpentData.flatMap((client) =>
    client.tasks.map((task) => task.displayValue)
  );

  const combinedData = showClientHour ? clientHourData : clientNoHourData;
  const stepSize = getMaxValue(combinedData);

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: t('graph.unit'),
        align: 'start',
        position: 'top',
        padding: {
          top: 0,
          bottom: 10,
        },
        font: {
          size: 16,
          weight: 'bold',
        },
        color: '#333',
      },
      legend: {
        display: false,
        position: 'top' as const,
      },
      tooltip: {
        backgroundColor: '#ffffff',
        titleColor: '#333333',
        bodyColor: '#333333',
        borderColor: '#cccccc',
        borderWidth: 1,
        padding: 10,
        titleFont: {
          size: 24,
          weight: 'bold',
        },
        bodyFont: {
          size: 24,
        },
        callbacks: {
          title: () => '',
          label: (context) => {
            if (showClientHour) {
              return context.dataset.label || ''; // When tasks are shown
            } else {
              const clientNoHourItem = clientNoHour[context.dataIndex];
              return clientNoHourItem ? clientNoHourItem.name : ''; // When tasks are hidden
            }
          },
          afterLabel: (context) => {
            if (!showClientHour) {
              // Calculate total duration when "Show Tasks" is unchecked
              const clientNoHourItem = clientNoHour[context.dataIndex];
              const totalDuration = clientNoHourItem?.hour_spent
                ? hoursValue(parseDurationToHours(clientNoHourItem.hour_spent))
                : t('graph.no_hours');
              return totalDuration; // Display the total duration
            } else {
              // Existing logic for showing task-specific hours when "Show Tasks" is checked
              const clientIndex = context.dataIndex;
              const taskIndex = context.datasetIndex;
              const task = hourSpentData[clientIndex]?.tasks?.find(
                (t) => t.taskId === context.dataset.label
              );
              return task ? hoursValue(task.actualValue) : '';
            }
          },
        },
      },
    },
    layout: {
      padding: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
    },
    scales: {
      x: {
        stacked: false,
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: false,
        },
        border: {
          color: 'black',
        },
      },
      y: {
        stacked: false,
        ticks: {
          stepSize: stepSize,
        },
        grid: {
          color: '#E0E0E0',
        },
        border: {
          color: 'black',
        },
        suggestedMax:
          Math.ceil(Math.max(...combinedData) / stepSize) * stepSize,
      },
    },
  };

  const clientWidth = 300;
  const visibleClients = 5;
  const totalClients = showClientHour ? clientHour.length : clientNoHour.length;
  const chartContainerWidth = totalClients * clientWidth;

  return (
    <div className="w-full">
      <div className="flex items-center justify-end">
        <label>
          <input
            type="checkbox"
            checked={showClientHour}
            onChange={(e) => onShowClientHourChange(e.target.checked)}
            className="mr-1"
          />
          {t('graph.show_task')}
        </label>
      </div>
      <div
        className="w-full overflow-x-auto"
        style={{
          maxWidth: `${visibleClients * clientWidth}px`,
          height: '36vh',
        }}
      >
        <div
          className="min-w-max h-full"
          style={{
            width: `${chartContainerWidth}px`,
          }}
        >
          <Bar data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

export default WorkPerformanceGraph;
